// =============================================================
// SEED / TEST DATA GENERATORS
// =============================================================
// Pure, side-effect-free builders that produce realistic mock data for
// pre-launch testing. They return plain data; the admin Tools tab decides what
// to do with it (push drops through the store, bulk-seed the rest).
//
// Everything is configurable from the admin UI:
//   • Drops  — number of campaigns, brands per campaign, boxes per drop,
//              verification method, auto-generated brand socials/website/logo,
//              and randomly generated catch questions per brand.
//   • Users  — count plus adjustable demographic distributions and funnel
//              rates (referrals, partner codes, redeemed %, reviewed %).
// =============================================================

import {
  generateDropCode,
  BATCH_CODES,
  levelForPoints,
  type VerificationMethod,
} from './store'
import type { Event, DropQuestion, DropProduct } from './store'
import type { SheetUser, SheetCatch, SheetReview } from './supabase'

const pick = <T,>(arr: T[], i: number): T => arr[i % arr.length]
const rand = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)]
const chance = (p: number) => Math.random() < p

const code = (len: number) => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let r = ''
  for (let i = 0; i < len; i++)
    r += chars.charAt(Math.floor(Math.random() * chars.length))
  return r
}

const pad = (n: number, width = 4) => String(n).padStart(width, '0')

// --- BRAND POOL -------------------------------------------------------------
// Each brand gets a deterministic handle/website/logo derived from its name so
// generated drops surface as believable "sponsored media".

const BRAND_POOL = [
  'NOCCO',
  'Barebells',
  'Estrella',
  'Vitamin Well',
  'OLIPOP',
  'Celsius',
  'RXBAR',
  'Oatly',
  'Marabou',
  'Cloetta',
  'Felix',
  'Zeta',
]

/** Slug a brand name into a URL-safe token (e.g. "Vitamin Well" → "vitaminwell"). */
const slug = (brand: string) => brand.toLowerCase().replace(/[^a-z0-9]/g, '')

/** Build believable socials + website + logo for a brand name. */
export function brandMeta(brand: string): {
  instagramUrl: string
  websiteUrl: string
  brandLogo: string
} {
  const s = slug(brand)
  return {
    instagramUrl: `https://instagram.com/${s}`,
    websiteUrl: `https://${s}.com`,
    // Deterministic placeholder logo keyed off the brand name.
    brandLogo: `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(
      brand,
    )}&backgroundType=solid`,
  }
}

// --- RANDOM QUESTION GENERATION ---------------------------------------------
// Questions are assembled from brand-aware templates so each brand's drops ask
// slightly different, on-brand things — useful for populating the answers panel.

const FLAVORS = ['Citrus 🍋', 'Berry 🫐', 'Tropical 🥭', 'Classic ✨', 'Mint 🌿']
const CHANNELS = [
  'A friend 👋',
  'Instagram 📸',
  'TikTok 🎵',
  'Saw the drop 🎣',
  'Just walking by 🚶',
]

/** Product-type-aware option pools so choices feel on-brand, not generic. */
const TYPE_VIBES: Record<string, string[]> = {
  drink: ['Ice-cold refresher 🧊', 'Pre-workout fuel 💪', 'All-day sipper 🥤', 'Treat myself moment 😋'],
  snack: ['Post-gym refuel 💪', 'Desk drawer stash 🗄️', 'On-the-go bite 🏃', 'Sweet-tooth fix 🍫'],
  coffee: ['Morning kickstart ☀️', 'Afternoon pick-me-up 🌤️', 'Study/work fuel 📚', 'Pure ritual ☕'],
  tea: ['Calm & unwind 🌙', 'Afternoon reset 🍵', 'Cold-brew refresher 🧊', 'Cozy moment 🧣'],
  supplement: ['Daily routine ✅', 'Training days 🏋️', 'Recovery 🛌', 'Just trying it 🧪'],
  product: ['Everyday go-to 🔁', 'Special occasion ✨', 'Gift for someone 🎁', 'First-time try 🆕'],
}

interface QuestionTemplate {
  /** Stable key used (with the brand slug) to build a scoped, collision-free id. */
  key: string
  build: (brand: string, productType: string) => DropQuestion
}

/**
 * Fun, interactive, brand + product-aware question templates. Each question's id
 * is scoped to the brand so answers aggregate cleanly per brand and never
 * collide across brands — keeping the demographics × answers data meaningful.
 */
const QUESTION_TEMPLATES: QuestionTemplate[] = [
  {
    key: 'flavor',
    build: (_brand) => ({
      id: '',
      label: 'Which flavor are you reeling for? 🎣',
      type: 'choice',
      options: [...FLAVORS].sort(() => Math.random() - 0.5).slice(0, 4),
    }),
  },
  {
    key: 'tried',
    build: (brand) => ({
      id: '',
      label: `Be honest — have you tried ${brand} before? 👀`,
      type: 'choice',
      options: ['Superfan already ❤️', 'Once or twice 🤔', 'First timer! 🆕'],
    }),
  },
  {
    key: 'vibe',
    build: (_brand, productType) => ({
      id: '',
      label: "When's this hitting the spot for you? ⏰",
      type: 'choice',
      options: TYPE_VIBES[productType] || TYPE_VIBES.product,
    }),
  },
  {
    key: 'hear',
    build: (_brand) => ({
      id: '',
      label: 'How did you stumble on this drop? 🔎',
      type: 'choice',
      options: CHANNELS,
    }),
  },
  {
    key: 'share',
    build: (brand) => ({
      id: '',
      label: `Would you tag a friend to try ${brand}? 🤝`,
      type: 'choice',
      options: ['Already sending it 📲', 'Maybe 🤷', 'Keeping it to myself 🙊'],
    }),
  },
  {
    key: 'open',
    build: (brand) => ({
      id: '',
      label: `One wish — what would make ${brand} even better? 💡`,
      type: 'text',
    }),
  },
]

/**
 * Generate `n` distinct, fun questions for a brand (capped at 5). Pass the
 * `productType` (e.g. 'drink', 'snack') so choices feel on-brand. Question ids
 * are prefixed with the brand slug so answers sync to the right brand.
 */
export function buildBrandQuestions(
  brand: string,
  n: number,
  productType = 'product',
): DropQuestion[] {
  const count = Math.max(0, Math.min(n, 5))
  const s = slug(brand) || 'brand'
  const shuffled = [...QUESTION_TEMPLATES].sort(() => Math.random() - 0.5)
  return shuffled.slice(0, count).map((t) => {
    const q = t.build(brand, productType)
    return { ...q, id: `q_${s}_${t.key}_${code(4)}` }
  })
}

// --- DROPS ------------------------------------------------------------------

const CAMPAIGN_THEMES = [
  'Summer Energy Drop',
  'Protein Pop-Up',
  'Snack Attack',
  'Hydrate & Go',
  'Soda Reinvented',
  'Fresh Start',
  'Late Night Cravings',
  'Festival Fuel',
]

const ADDRESSES = [
  'Kungsträdgården, Stockholm',
  'Sergels Torg, Stockholm',
  'Mariatorget, Stockholm',
  'Odenplan, Stockholm',
  'Stureplan, Stockholm',
  'Slussen, Stockholm',
  'Hötorget, Stockholm',
]

export interface DropGenConfig {
  /** Number of campaigns to create. */
  campaigns: number
  /** Brands participating in EACH campaign (each brand → one drop). */
  brandsPerCampaign: number
  /** Boxes per drop (total = boxesLeft at creation). */
  boxesPerDrop: number
  /** Redemption method applied to every generated drop. */
  verificationMethod: VerificationMethod
  /** How many random questions to attach per drop (0 = none). */
  questionsPerDrop: number
  /** Days between campaign start dates, beginning one week from today. */
  daysBetweenCampaigns?: number
}

export const DEFAULT_DROP_CONFIG: DropGenConfig = {
  campaigns: 2,
  brandsPerCampaign: 2,
  boxesPerDrop: 200,
  verificationMethod: 'both',
  questionsPerDrop: 2,
  daysBetweenCampaigns: 7,
}

/**
 * Build drops grouped into campaigns. Each campaign spans `brandsPerCampaign`
 * brands (one drop per brand), with auto-generated socials, website, logo and
 * random brand-aware catch questions.
 */
export function buildMockDrops(config: Partial<DropGenConfig> = {}): Event[] {
  const cfg: DropGenConfig = { ...DEFAULT_DROP_CONFIG, ...config }
  const gap = cfg.daysBetweenCampaigns ?? 7
  const today = new Date()
  const drops: Event[] = []
  let brandCursor = 0

  for (let c = 0; c < cfg.campaigns; c++) {
    const campaignName = pick(CAMPAIGN_THEMES, c)
    const d = new Date(today)
    d.setDate(today.getDate() + 7 + gap * c)
    const date = d.toISOString().slice(0, 10)

    for (let b = 0; b < cfg.brandsPerCampaign; b++) {
      const brand = pick(BRAND_POOL, brandCursor++)
      const meta = brandMeta(brand)
      const questions = buildBrandQuestions(brand, cfg.questionsPerDrop)
      const products: DropProduct[] = [
        { name: `${brand} Original`, brand },
        { name: `${brand} Zero`, brand },
      ]
      drops.push({
        id: `seed-drop-${c}-${b}-${code(4)}`,
        code: generateDropCode(),
        title: `${brand} — ${campaignName}`,
        brand,
        campaign: campaignName,
        verificationMethod: cfg.verificationMethod,
        description: `${brand} sampling drop as part of "${campaignName}" — grab your catch and tell us what you think.`,
        date,
        time: '12:00 - 14:00',
        address: pick(ADDRESSES, brandCursor),
        boxesLeft: cfg.boxesPerDrop,
        totalBoxes: cfg.boxesPerDrop,
        theme: 'Drop',
        type: 'reservation',
        requiredPoints: 0,
        customQuestions: questions,
        products,
        brandLogo: meta.brandLogo,
        instagramUrl: meta.instagramUrl,
        websiteUrl: meta.websiteUrl,
      })
    }
  }
  return drops
}

// --- USERS / CATCHES / REVIEWS ---------------------------------------------

const FIRST = [
  'Erik',
  'Anna',
  'Johan',
  'Sara',
  'Karl',
  'Emma',
  'Lars',
  'Maria',
  'Anders',
  'Linnea',
  'Oskar',
  'Elin',
  'Gustav',
  'Klara',
  'Henrik',
  'Ida',
  'Viktor',
  'Astrid',
  'Nils',
  'Saga',
]
const LAST = [
  'Andersson',
  'Johansson',
  'Karlsson',
  'Nilsson',
  'Eriksson',
  'Larsson',
  'Olsson',
  'Persson',
  'Svensson',
  'Gustafsson',
]
export const AGE_OPTIONS = ['Under 18', '18–24', '25–34', '35–44', '45+']
export const CITY_OPTIONS = [
  'Stockholm',
  'Göteborg',
  'Malmö',
  'Uppsala',
  'Solna',
  'Lund',
]
const OCCS = [
  'Student',
  'Employed full-time',
  'Employed part-time',
  'Self-employed',
  'Job seeking',
  'Parental leave',
  'Retired',
]
export const INTEREST_OPTIONS = [
  'Snacks & candy',
  'Drinks',
  'Health & fitness',
  'Beauty',
  'Tech',
  'Fashion',
  'Experiences',
  'Food',
]
const AREAS = [
  'Södermalm',
  'Östermalm',
  'Norrmalm',
  'Vasastan',
  'Kungsholmen',
  'Djurgården',
]
const PICKUPS = [
  'Instabox',
  'Budbee',
  'PostNord Box',
  'ICA',
  'Coop',
  'Pressbyrån',
]
const PRODUCTS = [
  { name: 'NOCCO Limón', brand: 'NOCCO' },
  { name: 'Soft Bar Caramel', brand: 'Barebells' },
  { name: 'Chili Chips', brand: 'Estrella' },
  { name: 'Vitamin Well Zero', brand: 'Vitamin Well' },
]
const PARTNER_POOL = ['SUMMER26', 'REDBULL', 'KTHFEST']

export interface UserGenConfig {
  /** Number of users to generate. */
  count: number
  /** Fraction (0–1) of users that entered a partner code. */
  partnerCodeRate: number
  /** Fraction (0–1) of users that were referred by an earlier user. */
  referralRate: number
  /** Fraction (0–1) of catches that get redeemed. */
  redeemRate: number
  /** Fraction (0–1) of redeemed catches that get reviewed. */
  reviewRate: number
  /** Restrict generated cities to this subset (empty = all). */
  cities?: string[]
  /** Restrict generated age ranges to this subset (empty = all). */
  ageRanges?: string[]
  /** Restrict generated interests to this subset (empty = all). */
  interests?: string[]
}

export const DEFAULT_USER_CONFIG: UserGenConfig = {
  count: 100,
  partnerCodeRate: 0.15,
  referralRate: 0.2,
  redeemRate: 0.7,
  reviewRate: 0.5,
  cities: [],
  ageRanges: [],
  interests: [],
}

export interface SeedBatch {
  users: SheetUser[]
  catches: SheetCatch[]
  reviews: SheetReview[]
}

const phoneFor = (i: number) => `070${pad(i, 7)}` // unique, stable per index

/**
 * Build users plus a realistic spread of catches and reviews tied to the
 * provided drops. Distributions and funnel rates come from `config`.
 */
export function buildMockUsers(
  drops: Event[],
  config: Partial<UserGenConfig> = {},
): SeedBatch {
  const cfg: UserGenConfig = { ...DEFAULT_USER_CONFIG, ...config }
  const cities = cfg.cities && cfg.cities.length ? cfg.cities : CITY_OPTIONS
  const ages =
    cfg.ageRanges && cfg.ageRanges.length ? cfg.ageRanges : AGE_OPTIONS
  const interestPool =
    cfg.interests && cfg.interests.length ? cfg.interests : INTEREST_OPTIONS

  const users: SheetUser[] = []
  const catches: SheetCatch[] = []
  const reviews: SheetReview[] = []
  const batchCode = BATCH_CODES[0].code
  const round = BATCH_CODES[0].round
  const usableDrops = drops.length > 0 ? drops : []

  // Pre-collect each drop's questions so answers reference real question ids.
  const dropQuestionMap = new Map<string, DropQuestion[]>()
  usableDrops.forEach((d) => dropQuestionMap.set(d.id, d.customQuestions || []))

  for (let i = 0; i < cfg.count; i++) {
    const phone = phoneFor(i)
    const name = `${pick(FIRST, i)} ${pick(LAST, i + 3)}`
    const catchCount = 1 + (i % 5) // spread 1..5 → new vs returning mix
    const invitedBy =
      i > 5 && chance(cfg.referralRate) ? phoneFor(i - 4) : undefined
    const partnerCode = chance(cfg.partnerCodeRate)
      ? rand(PARTNER_POOL)
      : undefined

    const drop = usableDrops.length ? pick(usableDrops, i) : undefined

    // Answer this drop's real questions for returning users so the Question
    // Answers panel has populated data on some profiles.
    const customAnswers: Record<string, string> = {}
    if (drop && catchCount >= 3) {
      for (const q of dropQuestionMap.get(drop.id) || []) {
        if (q.type === 'choice' && q.options?.length) {
          customAnswers[q.id] = rand(q.options)
        } else {
          customAnswers[q.id] = rand([
            'More flavors',
            'Bigger packs',
            'Lower price',
            'See it in more stores',
          ])
        }
      }
    }

    const points = (catchCount - 1) * 50 + (i % 3) * 10
    users.push({
      name,
      phone,
      ageRange: pick(ages, i),
      city: pick(cities, i),
      occupation: pick(OCCS, i),
      profession: i % 3 === 0 ? 'UX Designer' : 'Analyst',
      workArea: pick(AREAS, i + 1),
      favoriteArea: pick(AREAS, i),
      interests: [pick(interestPool, i), pick(interestPool, i + 3)],
      storePreference: pick(PICKUPS, i),
      customAnswers,
      invitedBy,
      partnerCode,
      points,
      level: levelForPoints(points),
      catchCount,
    })

    if (!drop) continue
    // Funnel: everyone catches; redeemRate redeemed; reviewRate of those reviewed.
    let status: SheetCatch['status'] = 'reserved'
    if (chance(cfg.redeemRate)) status = 'redeemed'
    if (status === 'redeemed' && chance(cfg.reviewRate)) status = 'reviewed'
    const reservationId = `seed-res-${i}-${code(5)}`
    catches.push({
      reservationId,
      userPhone: phone,
      event: drop.title,
      brand: drop.brand,
      batchCode,
      round,
      status,
      userCode: code(4),
      staffCode: code(4),
    })

    if (status === 'reviewed') {
      const dropProducts = drop.products?.length ? drop.products : PRODUCTS
      dropProducts.forEach((p, pi) => {
        reviews.push({
          reviewId: `seed-rev-${i}-${pi}-${code(4)}`,
          reservationId,
          userPhone: phone,
          product: p.name,
          brand: p.brand,
          taste: Math.random() > 0.2,
          packaging: Math.random() > 0.3,
          buyAgain: Math.random() > 0.35,
        })
      })
    }
  }

  return { users, catches, reviews }
}
